import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from "react";
import { gql, useReactiveVar } from "@apollo/client";
import Button, { ButtonVariant } from "./Button";
import { CSVLink, CSVDownload } from "react-csv";
import { DateTime } from "luxon";
import currency from "currency.js";
import { useFulfillmentDownloadCsvQuery } from "../types";
import { fulfillmentScreenQueryVar } from "./FulfillmentScreen";
import ErrorScreen from "./ErrorScreen";
import { currentShopifyVersionVar } from "../helpers/client";
gql `
query FulfillmentDownloadCSV($first: Int!, $after: String, $query: FulfillmentQuery!) {
  fulfillments(first: $first, after: $after, query: $query) {
    edges {
      node {
        ...FulfillmentItem
        tags
      }
    }
    pageInfo {
      endCursor
    }
  }
}
`;
const FulfillmentDownloadCSV = ({ businessId }) => {
    const fulfillmentScreenQuery = useReactiveVar(fulfillmentScreenQueryVar);
    let [first, setFirst] = useState(0);
    let [prevCursor, setPrevCursor] = useState(null);
    let [actuallyLoading, setActuallyLoading] = useState(false);
    let [csvData, setCsvData] = useState([]);
    // Trigger query mostly by turning setFirst to 10
    const fetchAllFulfillments = () => {
        setCsvData([]);
        setPrevCursor(null);
        setActuallyLoading(true);
        setFirst(50);
    };
    // Reset when query changes
    useEffect(() => {
        setFirst(0);
        setPrevCursor(null);
        setActuallyLoading(false);
        setCsvData([]);
    }, [fulfillmentScreenQuery]);
    // Fetch fulfillments manually whenver first changes, or when prevCursor changes
    const { error } = useFulfillmentDownloadCsvQuery({
        fetchPolicy: "no-cache",
        skip: !first,
        variables: {
            first: first,
            query: Object.assign(Object.assign({}, fulfillmentScreenQuery), { businessId }),
            after: prevCursor,
        },
        onCompleted: (data) => {
            // Ignore if we're not actually loading
            if (actuallyLoading) {
                // Merge results
                if ((data === null || data === void 0 ? void 0 : data.fulfillments.edges.length) > 0)
                    setCsvData([...csvData, ...data === null || data === void 0 ? void 0 : data.fulfillments.edges]);
                // If there are more fulfillments, fetch them
                if ((data === null || data === void 0 ? void 0 : data.fulfillments.edges.length) > 0 && (data === null || data === void 0 ? void 0 : data.fulfillments.pageInfo.endCursor) != prevCursor) {
                    setPrevCursor(data === null || data === void 0 ? void 0 : data.fulfillments.pageInfo.endCursor);
                }
                // If we're done, set first back to 0
                else {
                    setActuallyLoading(false);
                    setFirst(0);
                    setPrevCursor(null);
                }
            }
        }
    });
    if (actuallyLoading)
        return (_jsx(Button, { variant: ButtonVariant.Outlined, className: "w-auto inline-block my-2", disabled: true, children: "Preparing CSV export..." }));
    if (error)
        return _jsx(ErrorScreen, { message: error.message });
    const currentShopifyVersion = currentShopifyVersionVar();
    const csvHeaders = [
        "Fulfillment Date",
        "Status",
        "Shopify URL",
        "First Name",
        "Last Name",
        "Email Address",
        "Phone Number",
        "Tier Name",
        "Base Tier Price",
        "Total Price",
        "Benefit Name",
        "Variant ID",
        "Variant Description",
        "Variant Platform Integration ID",
        "Variant Platform Integration Name",
        "Shipping Name",
        "Shipping Address 1",
        "Shipping Address 2",
        "Shipping Address City",
        "Shipping Address State",
        "Shipping Address ZIP",
        "Shipping Address Country",
        "Shipping Notes",
        "Tags",
        "Past Due (as of export date)",
    ];
    if (currentShopifyVersion != 2) {
        csvHeaders.splice(9, 0, "Addon Price");
        csvHeaders.splice(16, 0, "Addons");
    }
    const fulfillmentCSVData = (input) => {
        return [
            csvHeaders,
            ...input.map((f) => {
                var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4;
                const addons = ((_a = f.addons) === null || _a === void 0 ? void 0 : _a.map((a) => a.description).join("; ")) || "";
                const tierPrice = currency((_d = (_c = (_b = f.membership) === null || _b === void 0 ? void 0 : _b.plan) === null || _c === void 0 ? void 0 : _c.price) === null || _d === void 0 ? void 0 : _d.amount, { fromCents: true });
                const addonPrice = currency((_e = f.extraCharge) === null || _e === void 0 ? void 0 : _e.amount, { fromCents: true });
                const totalPrice = tierPrice.add(addonPrice);
                const selectedProductSelections = (_f = f.productSelections) === null || _f === void 0 ? void 0 : _f.filter(productSelection => !!productSelection.variant);
                let row = [
                    f.fulfillmentDate,
                    f.isFulfilled ? "Fulfilled" : "Not Fulfilled",
                    f.shopifyUrl,
                    (_h = (_g = f.membership) === null || _g === void 0 ? void 0 : _g.user) === null || _h === void 0 ? void 0 : _h.firstName,
                    (_k = (_j = f.membership) === null || _j === void 0 ? void 0 : _j.user) === null || _k === void 0 ? void 0 : _k.lastName,
                    (_m = (_l = f.membership) === null || _l === void 0 ? void 0 : _l.user) === null || _m === void 0 ? void 0 : _m.emailAddress,
                    (_p = (_o = f.membership) === null || _o === void 0 ? void 0 : _o.user) === null || _p === void 0 ? void 0 : _p.phoneNumber,
                    (_r = (_q = f.membership) === null || _q === void 0 ? void 0 : _q.tier) === null || _r === void 0 ? void 0 : _r.name,
                    tierPrice.toString(),
                    totalPrice.toString(),
                    (_s = f.benefit) === null || _s === void 0 ? void 0 : _s.name,
                    selectedProductSelections === null || selectedProductSelections === void 0 ? void 0 : selectedProductSelections.map(productSelection => { var _a; return (_a = productSelection.variant) === null || _a === void 0 ? void 0 : _a.id; }).concat(!!((_t = f.variant) === null || _t === void 0 ? void 0 : _t.id) ? [f.variant.id] : []).join(", "),
                    selectedProductSelections === null || selectedProductSelections === void 0 ? void 0 : selectedProductSelections.map(productSelection => { var _a; return (_a = productSelection.variant) === null || _a === void 0 ? void 0 : _a.description; }).concat(!!((_u = f.variant) === null || _u === void 0 ? void 0 : _u.id) ? [f.variant.description] : []).join(", "),
                    selectedProductSelections === null || selectedProductSelections === void 0 ? void 0 : selectedProductSelections.filter(productSelection => { var _a; return (_a = productSelection.variant) === null || _a === void 0 ? void 0 : _a.platformId; }).map(productSelection => { var _a; return (_a = productSelection.variant) === null || _a === void 0 ? void 0 : _a.platformId; }).concat(!!((_v = f.variant) === null || _v === void 0 ? void 0 : _v.id) ? [f.variant.platformId] : []).join(", "),
                    selectedProductSelections === null || selectedProductSelections === void 0 ? void 0 : selectedProductSelections.filter(productSelection => { var _a; return (_a = productSelection.variant) === null || _a === void 0 ? void 0 : _a.platformId; }).map(productSelection => { var _a; return (_a = productSelection.variant) === null || _a === void 0 ? void 0 : _a.platformName; }).concat(!!((_w = f.variant) === null || _w === void 0 ? void 0 : _w.id) ? [f.variant.platformName] : []).join(", "),
                    (_x = f.address) === null || _x === void 0 ? void 0 : _x.shippingName,
                    (_y = f.address) === null || _y === void 0 ? void 0 : _y.line1,
                    (_z = f.address) === null || _z === void 0 ? void 0 : _z.line2,
                    (_0 = f.address) === null || _0 === void 0 ? void 0 : _0.city,
                    (_1 = f.address) === null || _1 === void 0 ? void 0 : _1.state,
                    (_2 = f.address) === null || _2 === void 0 ? void 0 : _2.zip,
                    (_3 = f.address) === null || _3 === void 0 ? void 0 : _3.country,
                    f.shippingNotes,
                    f.tags,
                    ((_4 = f.membership) === null || _4 === void 0 ? void 0 : _4.isPastDue) ? "Past Due" : "Up To Date",
                ];
                if (currentShopifyVersion != 2) {
                    row.splice(9, 0, addonPrice.toString());
                    row.splice(16, 0, addons);
                }
                return row;
            })
        ];
    };
    const csvFilename = `Withfriends Fulfillment Export ${DateTime.local().toSQLDate()}.csv`;
    const csvDataToDownload = csvData.length ? fulfillmentCSVData(csvData.map(e => e.node).sort((a, b) => a.fulfillmentDate < b.fulfillmentDate ? -1 : 1)) : null;
    return (_jsx(_Fragment, { children: csvData.length ? (_jsxs(_Fragment, { children: [
                /* @ts-ignore */
                _jsx(CSVDownload, { data: csvDataToDownload, filename: csvFilename, target: "_blank" }), 
                /* @ts-ignore */
                _jsx(CSVLink, { data: csvDataToDownload, filename: csvFilename, children: _jsx(Button, { variant: ButtonVariant.Salmon, className: "w-auto inline-block my-2", children: "Download all as CSV" }) })] })) : (_jsx(Button, { variant: ButtonVariant.Salmon, className: "w-auto inline-block my-2", onClick: fetchAllFulfillments, children: "Export all as CSV" })) }));
};
export default FulfillmentDownloadCSV;
